import { useEffect, useState } from "react";
import i18n from "@/i18n";
import { api, formatApiError } from "@/lib/api";
import { applyOverrides } from "@/lib/siteContent";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";

// Curated registry of editable static-text keys, grouped by page/section.
// `m` marks multiline (textarea) fields.
const REGISTRY = [
  {
    group: "Home — Hero",
    fields: [
      ["home.eyebrow", "Eyebrow"],
      ["home.title_a", "Title line 1"],
      ["home.title_b", "Title line 2"],
      ["home.title_c", "Title line 3"],
      ["home.intro", "Intro paragraph", true],
      ["home.cta_mission", "Button — Mission"],
      ["home.cta_chipmat", "Button — CHIPMAT"],
      ["home.cta_startups", "Button — Startups"],
    ],
  },
  {
    group: "Home — CHIPMAT block",
    fields: [
      ["home.chipmat_title", "Title"],
      ["home.chipmat_body", "Body", true],
      ["home.chipmat_cta", "Button"],
    ],
  },
  {
    group: "Home — Section headings",
    fields: [
      ["home.value_chain_title", "Value chain — title"],
      ["home.value_chain_desc", "Value chain — description", true],
      ["home.centres_title", "Centres — title"],
      ["home.centres_desc", "Centres — description", true],
      ["home.programmes_title", "Programmes — title"],
      ["home.incubation_title", "Incubation — title"],
      ["home.partners_title", "Partners — title"],
    ],
  },
  {
    group: "Home — Value chain steps",
    fields: [
      ["home.chain.research", "Research — label"],
      ["home.chain.research_d", "Research — detail"],
      ["home.chain.design", "Design — label"],
      ["home.chain.design_d", "Design — detail"],
      ["home.chain.prototype", "Prototype — label"],
      ["home.chain.prototype_d", "Prototype — detail"],
      ["home.chain.package", "Package — label"],
      ["home.chain.package_d", "Package — detail"],
      ["home.chain.test", "Test — label"],
      ["home.chain.test_d", "Test — detail"],
      ["home.chain.deploy", "Deploy — label"],
      ["home.chain.deploy_d", "Deploy — detail"],
    ],
  },
  {
    group: "Home — Key stats",
    fields: [
      ["home.stats.learners", "Stat 1 — label"],
      ["home.stats.professionals", "Stat 2 — label"],
      ["home.stats.ventures", "Stat 3 — label"],
      ["home.stats.tapeouts", "Stat 4 — label"],
    ],
  },
  {
    group: "Home — Roadmap",
    fields: [
      ["home.roadmap_eyebrow", "Eyebrow"],
      ["home.roadmap_title", "Title"],
      ["home.phase1", "Phase I — heading"],
      ["home.phase1_d", "Phase I — detail", true],
      ["home.phase2", "Phase II — heading"],
      ["home.phase2_d", "Phase II — detail", true],
      ["home.phase3", "Phase III — heading"],
      ["home.phase3_d", "Phase III — detail", true],
    ],
  },
  {
    group: "About / Footer",
    fields: [
      ["footer.about", "About Semicon Keralam", true],
      ["footer.govt", "Government line"],
      ["meta.tagline", "Brand tagline"],
      ["meta.agency", "Agency line"],
    ],
  },
  {
    group: "Page intros",
    fields: [
      ["initiatives_page.intro", "Initiatives — intro", true],
      ["board.intro", "Board — intro", true],
      ["startups.intro", "Startups — intro", true],
      ["careers.intro", "Careers — intro", true],
      ["gallery.intro", "Gallery — intro", true],
      ["contact.campus_body", "Contact — campus info", true],
    ],
  },
];

const LANGS = [
  ["en", "English"],
  ["ml", "മലയാളം"],
  ["hi", "हिन्दी"],
];

export default function SiteContentAdmin() {
  const [overrides, setOverrides] = useState({});
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const effective = (key, lng) => i18n.getFixedT(lng)(key);

  const load = () => {
    api.get("/site-content").then((r) => {
      setOverrides(r.data || {});
      // Seed drafts with the current effective value per language.
      const next = {};
      REGISTRY.forEach((g) =>
        g.fields.forEach(([key]) => {
          next[key] = {
            en: effective(key, "en"),
            ml: effective(key, "ml"),
            hi: effective(key, "hi"),
          };
        })
      );
      setDrafts(next);
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDraft = (key, lng, value) =>
    setDrafts((d) => ({ ...d, [key]: { ...d[key], [lng]: value } }));

  const save = async (key) => {
    setSavingKey(key);
    try {
      const payload = drafts[key] || { en: "", ml: "", hi: "" };
      await api.put(`/site-content/${key}`, payload);
      const nextOverrides = { ...overrides, [key]: payload };
      setOverrides(nextOverrides);
      applyOverrides(nextOverrides); // live-update the running app
      toast.success("Saved — live on the site");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const reset = async (key) => {
    setSavingKey(key);
    try {
      await api.delete(`/site-content/${key}`);
      const nextOverrides = { ...overrides };
      delete nextOverrides[key];
      setOverrides(nextOverrides);
      toast.success("Reset to default — reload to see the original text");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="sitecontent-admin">
      <p className="text-sm text-ink/60 max-w-2xl">
        Edit the words that appear on the public website. Changes save to all three languages and go live immediately.
        Leave a language blank to fall back to the default text.
      </p>

      {REGISTRY.map((g) => (
        <section key={g.group} className="border border-ink/10 bg-surface">
          <h3 className="font-display font-bold text-brand text-sm px-5 py-3 border-b border-ink/10 bg-white">{g.group}</h3>
          <div className="divide-y divide-ink/10">
            {g.fields.map(([key, label, multi]) => {
              const edited = !!overrides[key];
              const d = drafts[key] || { en: "", ml: "", hi: "" };
              return (
                <div key={key} className="p-5" data-testid={`sc-row-${key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-medium text-ink">
                      {label}
                      {edited && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-teal">edited</span>}
                    </div>
                    <div className="font-mono text-[10px] text-ink/30">{key}</div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    {LANGS.map(([lng, lbl]) => (
                      <label key={lng} className="block">
                        <span className="block text-[10px] font-mono uppercase tracking-widest text-ink/50 mb-1">{lbl}</span>
                        {multi ? (
                          <textarea
                            data-testid={`sc-input-${key}-${lng}`}
                            rows={4}
                            value={d[lng] || ""}
                            onChange={(e) => setDraft(key, lng, e.target.value)}
                            className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-3 py-2 text-ink text-sm resize-none"
                          />
                        ) : (
                          <input
                            data-testid={`sc-input-${key}-${lng}`}
                            value={d[lng] || ""}
                            onChange={(e) => setDraft(key, lng, e.target.value)}
                            className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-3 py-2 text-ink text-sm"
                          />
                        )}
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      data-testid={`sc-save-${key}`}
                      onClick={() => save(key)}
                      disabled={savingKey === key}
                      className="inline-flex items-center gap-1.5 bg-brand hover:bg-brand-dark disabled:opacity-50 text-white text-xs px-4 py-2"
                    >
                      <Save className="w-3.5 h-3.5" /> {savingKey === key ? "Saving…" : "Save"}
                    </button>
                    {edited && (
                      <button
                        data-testid={`sc-reset-${key}`}
                        onClick={() => reset(key)}
                        disabled={savingKey === key}
                        className="inline-flex items-center gap-1.5 border border-ink/15 hover:border-teal text-ink/70 text-xs px-4 py-2"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Reset
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
